import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AzureMarketPlaceComponent } from '@assetaccountant/core/components/azure-market-place/azure-market-place.component';
import { ExpiredOrganisationInviteComponent } from '@assetaccountant/core/components/expired-organisation-invite/expired-organisation-invite.component';
import { OffersComponent } from '@assetaccountant/core/components/offers/offers.component';
import { CreateFirstOrganisationComponent } from '@assetaccountant/core/components/onboarding/create-first-organisation/create-first-organisation.component';
import { StartComponent } from '@assetaccountant/core/components/onboarding/start/start.component';
import { UnauthorizedComponent } from '@assetaccountant/core/components/unauthorized/unauthorized.component';
import { CanActivateAuth } from '@assetaccountant/core/guards/auth.guard';
import { CanActivateRegisterAccess } from '@assetaccountant/core/guards/register-access.guard';
import { FyResolver } from '@assetaccountant/core/resolvers/fy.resolver';
import { OrganisationResolver } from '@assetaccountant/core/resolvers/organisation.resolver';
import { PeriodResolver } from '@assetaccountant/core/resolvers/period.resolver';
import { RegimeResolver } from '@assetaccountant/core/resolvers/regime.resolver';
import { RegisterResolver } from '@assetaccountant/core/resolvers/register.resolver';
import { DynamicsLaunchTenantComponent } from '@assetaccountant/integrations/components/dynamics/dynamics-launch-tenant/dynamics-launch-tenant.component';
import { DynamicsSelectEnvironmentComponent } from '@assetaccountant/integrations/components/dynamics/dynamics-select-environment/dynamics-select-environment.component';
import { QuickBooksConnectRealmComponent } from '@assetaccountant/integrations/components/quickbooks-connect-realm/quickbooks-connect-realm.component';
import { QuickBooksDisconnectRealmComponent } from '@assetaccountant/integrations/components/quickbooks-disconnect-realm/quickbooks-disconnect-realm.component';
import { QuickBooksLaunchRealmComponent } from '@assetaccountant/integrations/components/quickbooks-launch-realm/quickbooks-launch-realm.component';
import { QuickBooksSelectRegisterComponent } from '@assetaccountant/integrations/components/quickbooks-select-register/quickbooks-select-register.component';
import { SageV2ConnectCompanyComponent } from '@assetaccountant/integrations/components/sagev2/sagev2-connect-company/sagev2-connect-company.component';
import { XeroLaunchTenantComponent } from '@assetaccountant/integrations/components/xero-launch-tenant/xero-launch-tenant.component';
import { XeroSelectTenantComponent } from '@assetaccountant/integrations/components/xero-select-tenant/xero-select-tenant.component';
import { LogoutComponent } from './@public/components/logout.component';

const routes: Routes = [
  { path: '', redirectTo: 'organisations/-/dashboard', pathMatch: 'full' },
  { path: 'signin-oidc', component: QuickBooksSelectRegisterComponent }, // legacy
  { path: 'quickbooks-signin-oidc', component: QuickBooksSelectRegisterComponent },
  { path: 'xero-signin-oidc', component: XeroSelectTenantComponent },
  { path: 'dynamics-signin-oidc', component: DynamicsSelectEnvironmentComponent },
  { path: 'sage-signin-oidc', component: SageV2ConnectCompanyComponent },
  {
    path: 'xero',
    children: [
      {
        path: 'launch',
        component: XeroLaunchTenantComponent,
      },
    ],
  },
  {
    path: 'dynamics',
    children: [
      {
        path: 'launch',
        component: DynamicsLaunchTenantComponent,
      },
    ],
  },
  {
    path: 'quickbooks',
    children: [
      {
        path: 'launch',
        component: QuickBooksLaunchRealmComponent,
      },
      {
        path: 'disconnect',
        component: QuickBooksDisconnectRealmComponent,
      },
      {
        path: 'connect',
        component: QuickBooksConnectRealmComponent,
      },
    ],
  },
  {
    path: 'azure-market-place',
    component: AzureMarketPlaceComponent,
  },
  {
    path: 'offers/:campaignKey/:couponId',
    component: OffersComponent,
  },
  {
    path: 'offers/:campaignKey',
    component: OffersComponent,
  },
  {
    path: '',
    canActivate: [CanActivateAuth],
    runGuardsAndResolvers: 'always',
    children: [
      { path: 'logout', component: LogoutComponent },
      {
        path: 'invitations',
        loadChildren: () =>
          import('app/@assetaccountant/invitations/invitations.module').then(
            m => m.InvitationsModule,
          ),
      },
      {
        path: 'create-organisation-special/:campaignKey/:couponId',
        component: CreateFirstOrganisationComponent,
      },
      {
        path: 'create-organisation-special/:campaignKey',
        component: CreateFirstOrganisationComponent,
      },
      {
        path: 'create-organisation',
        component: CreateFirstOrganisationComponent,
      },
      {
        path: 'expired-organisation-invite',
        component: ExpiredOrganisationInviteComponent,
      },
      {
        path: 'unauthorized',
        component: UnauthorizedComponent,
        data: { chromeless: true },
      },
      {
        path: 'organisations/:organisationId',
        resolve: {
          organisation: OrganisationResolver,
        },
        runGuardsAndResolvers: 'always',
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
          { path: 'registers', pathMatch: 'full', redirectTo: 'dashboard' },
          {
            path: 'start',
            component: StartComponent,
          },
          {
            path: 'dashboard',
            loadChildren: () =>
              import(
                'app/@assetaccountant/organisation-dashboard/organisation-dashboard.module'
              ).then(m => m.OrganisationDashboardModule),
          },
          {
            path: 'settings',
            loadChildren: () =>
              import(
                'app/@assetaccountant/organisation-settings/organisation-settings.module'
              ).then(m => m.OrganisationSettingsModule),
          },
          {
            path: 'imports',
            loadChildren: () =>
              import('app/@assetaccountant/imports/imports.module').then(m => m.ImportsModule),
          },
          {
            path: 'reports',
            loadChildren: () =>
              import('@assetaccountant/reports/reports.module').then(m => m.ReportsModule),
          },
          {
            path: 'registers/:registerId',
            resolve: {
              register: RegisterResolver,
            },
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: 'start',
                component: StartComponent,
              },
              {
                path: '',
                canActivate: [CanActivateRegisterAccess],
                children: [
                  { path: '', pathMatch: 'full', redirectTo: 'regimes/-/periods/-/assets' },
                  {
                    path: 'imports',
                    loadChildren: () =>
                      import('app/@assetaccountant/imports/imports.module').then(
                        m => m.ImportsModule,
                      ),
                  },
                  {
                    path: 'journals',
                    loadChildren: () =>
                      import('app/@assetaccountant/journals/journals.module').then(
                        m => m.JournalsModule,
                      ),
                  },
                  {
                    path: 'settings',
                    loadChildren: () =>
                      import('@assetaccountant/register-settings/settings.module').then(
                        m => m.SettingsModule,
                      ),
                  },
                  {
                    path: 'regimes/:regimeId',
                    resolve: {
                      regime: RegimeResolver,
                    },
                    children: [
                      {
                        path: 'periods/:periodRange',
                        resolve: {
                          period: PeriodResolver,
                        },
                        children: [
                          {
                            path: '',
                            pathMatch: 'full',
                            redirectTo: 'assets',
                          },
                          {
                            path: 'dashboard',
                            redirectTo: 'assets',
                          },
                          {
                            path: 'assets',
                            loadChildren: () =>
                              import('app/@assetaccountant/assets/assets.module').then(
                                m => m.AssetsModule,
                              ),
                          },
                          {
                            path: 'assetgroups',
                            loadChildren: () =>
                              import('app/@assetaccountant/assetgroups/assetgroups.module').then(
                                m => m.AssetGroupsModule,
                              ),
                          },
                          {
                            path: 'reports',
                            loadChildren: () =>
                              import('@assetaccountant/reports/reports.module').then(
                                m => m.ReportsModule,
                              ),
                          },
                        ],
                      },
                      {
                        path: 'years/:fyRange',
                        resolve: {
                          period: FyResolver,
                        },
                        children: [
                          {
                            path: 'pools',
                            loadChildren: () =>
                              import('app/@assetaccountant/pools/pools.module').then(
                                m => m.PoolsModule,
                              ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '/organisations/-/dashboard' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: 'always',
      initialNavigation: 'disabled',
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
